/**
 * The fix tray on a fence (Figma 2:2, 3:35): `FixTrayHead` (the dot, "Fix on lines 14-16", and the tier's badge),
 * `TrayRow` (a label in a 72px column and what it holds), `Before` and `After` (the words struck in red and set in
 * green, as the print reads and as the fix reads) and `Nearest` (the book's own words offered). The tray's own
 * ground is `RailSection wash`.
 */
import type { ReactNode } from 'react';
import { CONTROL, RADIUS, RULE, SPACE, TEXT, WEIGHT, usePalette } from '../theme';
import { Chip, OUTLINE, toneOf } from './chip';

export function FixTrayHead({ title, badge }: { title: ReactNode; badge: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
      <span aria-hidden="true" style={{ width: CONTROL.dot, height: CONTROL.dot, borderRadius: RADIUS.pill, background: p.absence, flexShrink: 0 }} />
      <h3 style={{ margin: 0, fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink, lineHeight: 'normal' }}>{title}</h3>
      <span style={{ flex: 1 }} />
      <Chip look={toneOf('waiting')} size="row">
        {badge}
      </Chip>
    </div>
  );
}

export function TrayRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, fontSize: TEXT.control, lineHeight: 'normal' }}>
      <span style={{ width: RULE.label, flexShrink: 0, color: p.inkMuted }}>{label}</span>
      <span style={{ color: p.ink, display: 'flex', gap: SPACE.tight, flexWrap: 'wrap', minWidth: 0 }}>{children}</span>
    </div>
  );
}

function Words({ children, ground, ink }: { children: ReactNode; ground: string; ink: string }) {
  return (
    <span style={{ padding: `${SPACE.xxs}px ${SPACE.tight}px`, borderRadius: RADIUS.sm, background: ground, color: ink, fontSize: TEXT.caption, lineHeight: 'normal' }}>{children}</span>
  );
}

/** The words as the reader read them. */
export function Before({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <Words ground={p.redWash} ink={p.redInk}>
      {children}
    </Words>
  );
}

/** The words as the fix reads them. */
export function After({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <Words ground={p.approvedWash} ink={p.approvedInk}>
      {children}
    </Words>
  );
}

export function Nearest({ words }: { words: readonly string[] }) {
  return (
    <>
      {words.map((word) => (
        <Chip key={word} look={OUTLINE}>
          {word}
        </Chip>
      ))}
    </>
  );
}
